// Payment MODE — "does this restaurant move real money?"
//
// This is a SEPARATE axis from routing, and the separation is load-bearing.
// `IPaymentRoutingSettings` answers *which provider* processes a transaction.
// Mode answers *whether the money is real*. They are orthogonal: a sandbox
// restaurant still routes to TRX, it just routes to TRX's cert gateway with a
// platform credential. Folding mode into the routing settings would put a
// "fake money" flag inside the type whose own header warns that re-introducing
// a default there is what once silently pushed TRX restaurants onto another processor.
//
// ## The model, and why it is shaped like Stripe's
//
// A merchant in `sandbox` gets the whole product with none of the consequences:
// the same POS, the same checkout, the same receipts, the same books — and a
// gateway that never moves a cent. That is what makes it usable for a live
// demo, for a merchant exploring the product before underwriting completes, and
// for training staff on a real terminal.
//
// ## The gateway and the credential are ONE decision
//
// TRX runs two fully-isolated gateways with NO shared identity space
// (`TRX_MIGRATION.md` §1): cert is `gateway.trxservices.net`, production is
// `gateway.trxservices.com`. A credential issued for one is a phantom account
// on the other, which surfaces as `X3 / "TranAction Not Supported"`.
//
// So "which gateway" and "which credential" can never be resolved separately —
// resolving them apart is precisely how you get a live credential posted to
// cert (nothing settles, no money moves, and no error says so). Everything
// downstream that keys off the environment — most importantly the IDTECH
// `SwipeDevice` reader code, "13" on cert and "14" on production, which selects
// the DUKPT BDK the gateway decrypts with — must key off this SAME resolution
// or the reader payload is rejected.
//
// Before this existed the gateway was chosen ONCE per deployment from
// `TRX_API_URL` and captured in the `TrxHttpClient` constructor (a singleton),
// so a sandbox merchant was unrepresentable on a production deployment: its
// cert credential would be posted to the production host.

/**
 * Every payment mode, as a tuple.
 *
 * Source of truth for the Zod enum in `@order-up/schemas`, which spreads it
 * (`z.enum([...PAYMENT_MODES])`) exactly as `PAYMENT_PROVIDERS` and
 * `PAYMENT_RAILS` are spread — so a mode added here propagates to validation
 * automatically instead of being accepted by the model and rejected by the
 * schema.
 */
export const PAYMENT_MODES = ["live", "sandbox"] as const;

/**
 * Whether a restaurant's payments move real money.
 *
 * - `live` — the merchant's own gateway credentials, real funds, real
 *   settlement.
 * - `sandbox` — the platform's cert credentials on the cert gateway. Every
 *   surface behaves normally; nothing is ever charged and nothing settles.
 */
export type PaymentMode = (typeof PAYMENT_MODES)[number];

/**
 * The mode a restaurant is in when its document says nothing.
 *
 * **`live`, and this direction is a safety property, not a preference.** Every
 * restaurant that existed before this field carries no value, and every one of
 * them is genuinely live. Defaulting an absent field to `sandbox` would divert
 * real card traffic to the cert gateway for the entire existing estate at once
 * — nothing would settle, and the failure is silent at the point of sale
 * because cert happily returns an approval.
 *
 * Sandbox is therefore only ever reached by an EXPLICIT value. There is no
 * inference, no heuristic, and deliberately no derivation from the `_demo_`
 * document-id prefix: sandbox is a product capability any merchant may use, not
 * a property of demo tenants, and a prefix-keyed check would also silently
 * never fire on a guid (see `DEMO_TENANT_ARCHITECTURE.md` § 1).
 */
export const DEFAULT_PAYMENT_MODE: PaymentMode = "live";

/** Narrow an unknown/absent stored value to a {@link PaymentMode}. */
export function isPaymentMode(value: unknown): value is PaymentMode {
  return (PAYMENT_MODES as readonly string[]).includes(value as string);
}

/**
 * Resolve a restaurant's payment mode from its stored value.
 *
 * An unrecognised value resolves to {@link DEFAULT_PAYMENT_MODE} rather than
 * throwing: a typo in a Firestore document must not take a live restaurant's
 * payments offline, and `live` is the mode that keeps real money working. A
 * restaurant that MEANT to be sandboxed and is misspelled will charge real
 * cards — which is why the writer side is schema-validated, and why the
 * resolver logs.
 */
export function resolvePaymentMode(stored: unknown): PaymentMode {
  return isPaymentMode(stored) ? stored : DEFAULT_PAYMENT_MODE;
}

/** True when this mode must never reach a real merchant account. */
export function isSandboxMode(mode: PaymentMode): boolean {
  return mode === "sandbox";
}
